<template>
  <page class="contacts" :loading="!contacts">
    <h2>Contacts</h2>

    <contacts-filter-container :loading="$apollo.queries.contacts.loading" :show-filters-on-load="contacts && contacts.totalContacts > 0"
      :where="filterContactsWhere" @whereUpdated="where => updateContactsFilter({ where, page: 1 })"
      :results="contacts" @pageUpdated="page => updateContactsFilter({ page })">

        <template v-slot:results="{ edges }">
          <b-table class="mt-3" striped hover responsive :fields="fields" :items="edges.map(({ entry }) => entry)">
            <template #cell(avatar)="{ item: contact, value }">
              <contact-modal :contact="contact" :id="`contact-modal-${contact.id}`"/>
              <img class="contact-picture" v-b-modal="`contact-modal-${contact.id}`" :src="`/static/images/avatars/main${value || 1}.png`"/>
            </template>
            <template #cell(profiles)="{ item }">
             <a class="mr-1" v-for="profile in item.profiles" target="_blank" :key="profile.domain" :href="`https://${profile.clean}`">
               <font-awesome-icon :icon="[ 'fab', icon(profile.domain) ]"/>
             </a>
            </template>
            <template #cell(referOpt)="{ item }">
              <b-button variant="primary" @click="openJobSelectModal(item.id)">Refer</b-button>
            </template>
          </b-table>
        </template>

        <template v-slot:no-results>
          <b-card class="my-3 py-3 px-5 filter-row no-results" body-class="p-0 text-center" v-if="contacts && contacts.totalContacts > 0">
            <template v-if="isRecruiter">
              <h3>There aren't any synced any contacts yet</h3>
              <h4>Ask employees to sync their connections or <router-link  :to="{ name: 'ManageUserInvites' }">invite some more</router-link> to the platform.</h4>
            </template>
            <template v-else>
              <div class="question-circle bg-primary-a2 mb-4"><span class="primary-color">?</span></div>
              <h3>We found no results based on your filters</h3>

              <h4 class="mt-4">You can share or upload a CV directly below</h4>
              <share :url="shareUrl" @track="trackShareEvent"/>

              <h3 class="my-3">or</h3>

              <div class="upload-cv-button d-flex justify-content-center">
                <b-btn variant="primary" @click.prevent="() => showApplyBox = true">Upload a CV</b-btn>
              </div>
              <p class="mt-2" v-if="showApplyBox">Please enter your contacts details on the right hand side.</p>
            </template>
          </b-card>
          <b-card class="my-3 py-3 px-5 filter-row" body-class="p-0 text-center" v-else>
            <template v-if="isRecruiter">
              <h3>There aren't any synced contacts yet</h3>
              <h4>Ask employees to sync their connections or <a href="#" to="{ name: 'ManageUserInvites' }">invite some more</a> to the platform.</h4>
            </template>
            <template v-else>
              <h3 class="mt-4">You can refer this job by sharing with your contacts:</h3>
              <share :url="shareUrl" @track="trackShareEvent"/>

              <h4 class="my-3">or</h4>

              <h3>Sync your contacts and we'll match them to jobs:</h3>
              <div class="upload-selector p-0 p-md-3 d-flex justify-content-between align-items-center">
                <b-btn :style="{ 'background-color': '#0e76a8', 'border-width': 0, 'color': '#fff' }"
                  :to="{ ...contactSyncLink, query: { provider: 'linkedin' } }" class="mr-1 mr-md-5">Connect to LinkedIn</b-btn>
                <b-btn :style="{ 'background-color': '#dc4e41', 'border-width': 0, 'color': '#fff' }"
                  :to="{ ...contactSyncLink, query: { provider: 'google' } }" class="mr-1 mr-md-5">Connect to Google</b-btn>
                <b-btn :style="{ 'background-color': '#0072c6', 'border-width': 0, 'color': '#fff' }"
                  :to="{ ...contactSyncLink, query: { provider: 'microsoft' } }">Connect to Microsoft</b-btn>
              </div>

              <h4 class="my-3">or</h4>

              <h3>Upload a CV directly:</h3>
              <div class="upload-cv-button d-flex justify-content-center">
                <b-btn variant="primary" @click.prevent="() => showApplyBox = true">Upload a CV</b-btn>
              </div>
              <p class="mt-2" v-if="showApplyBox">Please enter your contacts details on the right hand side.</p>
            </template>
          </b-card>
        </template>
    </contacts-filter-container>

    <!-- job select modal -->
    <b-modal id="jobSelectModal" size="lg" lazy hide-footer hide-header modal-class="job-select-modal">
      <jobs-filter class="p-4" layout="select-modal" v-if="jobs" :loading="$apollo.queries.jobs.loading"
        :where="filterJobsWhere" @whereUpdated="filterJobsWhereUpdated" :whereCustomFields="jobs.filterJobsWhereCustomFields"
        :results="jobs.jobConnections" @pageUpdated="filterJobsPageUpdated"
        :order="filterJobsOrder" @orderUpdated="filterJobsOrderUpdated" @selectJob="selectJob($event)" />
    </b-modal>

    <!-- refer contact modal -->
    <b-modal v-if="selectedJob" id="referContactModal" size="lg" lazy hide-footer hide-header modal-class="contact-refer-modal">
      <b-row>
        <b-col class="px-5 py-3">
          <strong v-if="selectedContact" style="font-size: 22px; line-height: 32px">Refer {{selectedContact.fullName}} for {{selectedJob.title}} by uploading a CV on their behalf or sharing the role with them</strong>
          <strong v-else style="font-size: 22px; line-height: 32px">Refer someone for {{selectedJob.title}} by uploading a CV on their behalf or sharing the role with them</strong>
        </b-col>
      </b-row>
      <b-row>
        <b-col class="px-5 pt-2">
          <share :shareCode="selectedJob.shareCode" :job="selectedJob" />
        </b-col>
      </b-row>
      <b-row>
        <b-col class="px-5 pb-2 d-flex align-items-center justify-content-center">
          <strong style="font-size: 22px; line-height: 32px">or</strong>
        </b-col>
      </b-row>
      <b-row>
        <b-col class="px-5 py-2 d-flex align-items-center justify-content-center">
          <b-button class="px-3 py-2 mt-3" variant="primary" @click="openUploadCVModal()">Upload a CV</b-button>
        </b-col>
      </b-row>
      <b-row>
        <b-col class="d-flex align-items-end justify-content-end">
          <b-btn variant="outline-primary" class="float-right mt-3" @click="$bvModal.hide('referContactModal')">Close</b-btn>
        </b-col>
      </b-row>
    </b-modal>

    <!-- upload CV -->
    <b-modal v-if="selectedJob" id="uploadCVModal" size="lg" lazy hide-footer hide-header modal-class="upload-cv-modal">
      <b-row :title="`Already have ${isRecruiter ? 'candidate' : 'contact'} details?`" class="p-3">
        <b-col>
          <p>Submit an application on behalf of a <template v-if="isRecruiter">candidate</template><template v-else>friend or contact</template>.</p>
          <b-alert show variant="success" v-if="applyResult === 'OK'">
            The application has been submitted. Thank you!
          </b-alert>
          <template v-else>
            <b-alert show variant="danger" v-if="applyResult === 'ERR'">
              Sorry an error has occurred, please try again.
            </b-alert>
            <apply-box class="mt-3" :jobID="selectedJob.id" :userID="user.id" on-behalf @submit="applySubmit"/>
          </template>
        </b-col>
      </b-row>
    </b-modal>

  </page>
</template>

<script>
import { mapRoot } from '@src/mixins/root';
import ContactsIndexEmployeesQuery from '@/graphql/queries/ContactsIndexEmployees.gql';
import ContactsIndexRecruitersQuery from '@/graphql/queries/ContactsIndexRecruiters.gql';
import JobApplicationCreateMutation from '@/graphql/mutations/JobApplicationCreate.gql';
import ContactsIndexJobsModalQuery from '@/graphql/queries/ContactsIndexJobsModal.gql';
import ms from 'ms';
import ContactsFilterContainer from '@/components/contacts-filter/container';
import ContactModal from '@/modals/contact-profile';
import Share from '@/components/contacts/share';
import JobsFilter from '@/components/jobs/FilterContainer';

import Page from '@/components/Page';

const ICONS = {
  'linkedin.com': 'linkedin',
  'facebook.com': 'facebook-square',
  'instagram.com': 'instagram',
};

export default {
  components: {
    Page,
    ContactsFilterContainer,
    ContactModal,
    Share,
    JobsFilter,
    ApplyBox: () => import('@/components/jobs/apply'),
  },
  data: () => ({
    err: null,
    currentPage: 1,
    selectedContact: null,
    selectedJob: null,
    applyResult: null,
    filterContactsWhere: {},
    filterContactsPage: 1,
    filterContactsLimit: 20,
    filterJobsWhere: {
      status: 'ACTIVE',
    },
    filterJobsPage: 1,
    filterJobsLimit: 20,
    filterJobsOrder: 'CREATED_DESC',
  }),
  metaInfo: {
    title: 'Contacts',
  },
  apollo: {
    contacts: {
      query() {
        return this.isRecruiter ? ContactsIndexRecruitersQuery : ContactsIndexEmployeesQuery;
      },
      variables() {
        const { filterContactsWhere, filterContactsPage, filterContactsLimit } = this;
        return { filterContactsWhere, filterContactsPage, filterContactsLimit };
      },
      update({ company, user }) {
        const { contactConnections: contacts, totalContacts: { total } } = this.isRecruiter ? company : user;
        // console.log(contacts);
        // contacts.edges = Array.isArray(contacts.edges) ? contacts.edges.map(({ entry }) => entry) : [];
        contacts.totalContacts = total;
        return contacts;
      },
      error(err) {
        this.$notifyErr(err, { prepend: 'Failed to load contacts:' });
      },
      debounce: ms('1s'),
      deep: true,
      fetchPolicy: 'network-only',
    },
    jobs: {
      query: ContactsIndexJobsModalQuery,
      variables() {
        const { filterJobsWhere, filterJobsPage, filterJobsLimit, filterJobsOrder } = this;
        return { filterJobsWhere, filterJobsPage, filterJobsLimit, filterJobsOrder };
      },
      update({ company: { jobConnections, filterJobsWhereCustomFields } }) {
        jobConnections.filterJobsWhereCustomFields = filterJobsWhereCustomFields;
        return jobConnections;
      },
      fetchPolicy: 'network-only',
      deep: true,
      throttle: ms('1s'),
      error(err) {
        this.err = `${err}`;
      },
      skip: true,
    },
  },
  computed: {
    ...mapRoot([ 'user', 'isRecruiter' ]),
    fields() {
      const fields = [
        {
          headerTitle: '',
          label: '',
          key: 'avatar',
        },
        {
          headerTitle: 'Name',
          label: 'Name',
          key: 'fullName',
        },
        {
          headerTitle: 'Job Ttile',
          label: 'Job Title',
          key: 'latestEmployment.title',
        },
        {
          headerTitle: 'Company',
          label: 'Company',
          key: 'latestEmployment.companyName',
        },
        {
          headerTitle: 'Social',
          label: 'Social',
          key: 'profiles',
          formatter: val => Array.isArray(val) ? val : []
        },
        {
          headerTitle: 'Location',
          label: 'Location',
          key: 'locations',
          formatter: val => Array.isArray(val) ? (val[0] ? val[0].city : '') : ''
        },
        // {
        //   headerTitle: 'Matches',
        //   label: 'Matches',
        //   key: 'jobs',
        //   sortByFormatted: val => Array.isArray(val) ? val.length : 0,
        //   formatter: val => Array.isArray(val) && val.length ? `${val.length} Matches` : ''
        // },
        // {
        //   headerTitle: 'Refer',
        //   label: 'Refer',
        //   key: 'referOpt',
        // },
        // {
        //   headerTitle: 'Profile',
        //   label: 'Profile',
        //   key: 'profileOpt',
        // },
      ];

      if (this.isRecruiter) {
        // Remove Name field
        fields.splice(1, 1);
        // Remove Social field
        fields.splice(4 - 1, 1);
      }

      return fields;
    },
  },
  methods: {
    icon(domain) {
      return ICONS[domain];
    },
    updateContactsFilter({ where, page }) {
      if (where) {
        this.filterContactsWhere = where;
      }
      if (page) {
        this.filterContactsPage = page;
      }
    },
    openReferContactModal({ contactID, jobID }) {
      const { contacts } = this;
      this.selectedContact = contacts.edges.find(({ entry }) => entry.id === contactID);
      this.selectedJob = this.selectedContact.jobs.find(el => el.id === jobID);
      this.$bvModal.hide('contactModal');
      setTimeout(() => {
        this.$bvModal.show('referContactModal');
      }, 300);
    },
    openUploadCVModal() {
      this.$bvModal.hide('referContactModal');
      this.$bvModal.show('uploadCVModal');
    },
    openJobSelectModal(contactID) {
      if (contactID) {
        const { contacts } = this;
        this.selectedContact = contacts.edges.find(({ entry }) => entry.id === contactID);
      } else {
        this.selectedContact = null;
      }
      this.$bvModal.show('jobSelectModal');
    },
    selectJob(job) {
      this.selectedJob = job;
      this.$bvModal.hide('jobSelectModal');
      setTimeout(() => {
        this.$bvModal.show('referContactModal');
      }, 300);
    },
    async applySubmit(form) {
      try {
        const { fullName, emailAddress, phoneNumber, CV, userAnswers, termsAgreed } = form;

        await this.$apollo.mutate({
          mutation: JobApplicationCreateMutation,
          variables: {
            create: {
              job: { id: this.selectedJob.id },
              user: { id: this.user.id },
              fullName,
              emailAddress,
              phoneNumber,
              source: 'JOB',
              CV,
              userAnswers: userAnswers || null,
              termsAgreed,
            },
          },
        });

        this.applyResult = 'OK';
      } catch (err) {
        this.$log.error(err);
        this.applyResult = 'ERR';
      }
    },
    filterJobsPageUpdated(newPage) {
      this.filterJobsPage = newPage;
      this.$scrollTo('body');
    },
    filterJobsWhereUpdated(newWhere) {
      this.filterJobsWhere = newWhere;
      this.filterJobsPage = 1;
      this.$scrollTo('body');
    },
    filterJobsOrderUpdated(newOrder) {
      this.filterJobsOrder = newOrder;
      this.filterJobsPage = 1;
      this.$scrollTo('body');
    },
  }
};
</script>

<style lang="scss">
.page.contacts {
  .contact-picture {
    width: 40px;
    height: 40px;
  }
  .matched-jobs-col {
    min-width: 120px;
  }
  .table-cell {
    vertical-align: middle;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/contacts/contactsIndex.vue